import React, { FC, useMemo } from 'react';
import { Box } from '@mui/material';
import { ArcElement, Chart as ChartJS, ChartData, ChartOptions, Legend, Plugin, Tooltip } from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { Doughnut } from 'react-chartjs-2';
import {
  SQUAD_PIE_DEFAULT_HEIGHT_PX,
  SQUAD_PIE_INSIDE_LABEL_COLOR,
  SQUAD_PIE_INSIDE_LABEL_STROKE,
  SQUAD_PIE_INSIDE_LABEL_STROKE_WIDTH,
  SQUAD_PIE_LEGEND_FONT_SIZE_PX,
  SQUAD_PIE_LEGEND_MARK_PX,
  SQUAD_PIE_OUTSIDE_LABEL_THRESHOLD_PCT,
  SQUAD_PIE_SLICE_LABEL_FONT_SIZE_PX,
  SQUAD_PORTFOLIO_PIE_FONT_FAMILY,
  SQUAD_PORTFOLIO_TEXT_COLOR,
  formatPieSliceDataLabel,
  generateGradientColors,
  getSliceSharePercent,
  isPieOutsideLabel,
  squadPieLayoutPadding,
} from './squadPortfolioChartUtils';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

type ChartItem = { label: string; value: number };

type Props = {
  data: ChartItem[];
  id?: string;
  /** DOM id on pie canvas wrap only (legend is rendered natively in PDF). */
  captureId?: string;
  /** Primary brand color — gradient palette is generated from this when `colors` is empty. */
  primaryColor: string;
  /** Excel Farbtheme colors (widget-mode parity). Falls back to primary gradient. */
  colors?: string[];
  height?: number;
};

function getCtxValue(ctx: Context): number {
  const arr = ctx.dataset?.data as unknown[];
  const raw = Array.isArray(arr) ? arr[ctx.dataIndex] : 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

const pieConnectorPlugin: Plugin<'doughnut'> = {
  id: 'squadPortfolioPieConnectorPlugin',
  afterDatasetsDraw(chart) {
    const meta = chart.getDatasetMeta(0);
    const dataset = chart.data.datasets[0]?.data ?? [];
    if (!meta?.data?.length || !dataset.length) return;

    const numeric = dataset.map((v) => Number(v) || 0);
    const total = numeric.reduce((s, v) => s + (Number.isFinite(v) ? Math.max(v, 0) : 0), 0);
    if (total <= 0) return;

    const { ctx } = chart;
    ctx.save();
    ctx.strokeStyle = SQUAD_PORTFOLIO_TEXT_COLOR;
    ctx.lineWidth = 1;
    ctx.lineCap = 'round';

    meta.data.forEach((arc, idx) => {
      const a = arc as unknown as {
        startAngle: number;
        endAngle: number;
        outerRadius: number;
        x: number;
        y: number;
      };
      const value = numeric[idx];
      const pct = getSliceSharePercent(value, total);
      if (!Number.isFinite(pct) || pct >= SQUAD_PIE_OUTSIDE_LABEL_THRESHOLD_PCT || value <= 0) return;

      const angle = (a.startAngle + a.endAngle) / 2;
      const r1 = a.outerRadius + 2;
      const r2 = a.outerRadius + 14;
      const x1 = a.x + Math.cos(angle) * r1;
      const y1 = a.y + Math.sin(angle) * r1;
      const x2 = a.x + Math.cos(angle) * r2;
      const y2 = a.y + Math.sin(angle) * r2;

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    });

    ctx.restore();
  },
};

export const SquadPortfolioPieChart: FC<Props> = ({
  data,
  id,
  captureId,
  primaryColor,
  colors: colorsProp,
  height = SQUAD_PIE_DEFAULT_HEIGHT_PX,
}) => {
  const colors = useMemo(() => {
    if (Array.isArray(colorsProp) && colorsProp.length > 0) return colorsProp;
    return generateGradientColors(primaryColor, Math.max(data.length, 1));
  }, [colorsProp, primaryColor, data.length]);

  const series = useMemo(() => data.map((item) => +item.value), [data]);

  const total = useMemo(
    () => series.reduce((s, v) => s + (Number.isFinite(v) ? Math.max(v, 0) : 0), 0),
    [series]
  );

  const chartData = useMemo<ChartData<'doughnut'>>(
    () => ({
      labels: data.map((item) => item.label),
      datasets: [
        {
          data: series,
          backgroundColor: colors,
          borderWidth: 0,
          hoverOffset: 2,
          cutout: '58%',
        },
      ],
    }),
    [data, series, colors]
  );

  const chartOptions = useMemo<ChartOptions<'doughnut'>>(
    () => ({
      font: { family: SQUAD_PORTFOLIO_PIE_FONT_FAMILY },
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: squadPieLayoutPadding(height),
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: {
          display: (ctx: Context) => {
            const value = getCtxValue(ctx);
            return getSliceSharePercent(value, total) > 0;
          },
          color: (ctx: Context) => {
            const value = getCtxValue(ctx);
            return isPieOutsideLabel(value, total) ? SQUAD_PORTFOLIO_TEXT_COLOR : SQUAD_PIE_INSIDE_LABEL_COLOR;
          },
          textStrokeColor: SQUAD_PIE_INSIDE_LABEL_STROKE,
          textStrokeWidth: (ctx: Context) => {
            const value = getCtxValue(ctx);
            return isPieOutsideLabel(value, total) ? 0 : SQUAD_PIE_INSIDE_LABEL_STROKE_WIDTH;
          },
          font: {
            size: SQUAD_PIE_SLICE_LABEL_FONT_SIZE_PX,
            weight: 600,
            family: SQUAD_PORTFOLIO_PIE_FONT_FAMILY,
          },
          formatter: (value: unknown) => formatPieSliceDataLabel(Number(value), total),
          anchor: (ctx: Context) => {
            const value = getCtxValue(ctx);
            return isPieOutsideLabel(value, total) ? 'end' : 'center';
          },
          align: (ctx: Context) => {
            const value = getCtxValue(ctx);
            return isPieOutsideLabel(value, total) ? 'end' : 'center';
          },
          offset: (ctx: Context) => {
            const value = getCtxValue(ctx);
            return isPieOutsideLabel(value, total) ? 10 : 0;
          },
          clamp: true,
          clip: false,
        },
      },
      animation: false,
    }),
    [height, total]
  );

  return (
    <Box
      className="squad-portfolio-pie-chart custom-portfolio-pie-chart"
      lang="de"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.45fr) minmax(0, 0.55fr)',
        gap: '6px',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        fontFamily: SQUAD_PORTFOLIO_PIE_FONT_FAMILY,
        color: SQUAD_PORTFOLIO_TEXT_COLOR,
      }}
    >
      <Box
        id={captureId}
        className="pie-chart-canvas-wrap"
        sx={{
          minWidth: 0,
          width: '100%',
          height,
          minHeight: height,
          maxHeight: height,
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        <Doughnut
          id={id}
          data={chartData}
          options={chartOptions}
          plugins={[pieConnectorPlugin]}
        />
      </Box>

      <Box
        className="pie-chart-legend"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          minWidth: 0,
          fontSize: SQUAD_PIE_LEGEND_FONT_SIZE_PX,
          lineHeight: 1.2,
        }}
      >
        {data.map((item, index) => (
          <Box
            key={index}
            className="pie-chart-legend-item"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '5px',
              minWidth: 0,
            }}
          >
            <Box
              className="pie-chart-legend-mark"
              sx={{
                width: SQUAD_PIE_LEGEND_MARK_PX,
                height: SQUAD_PIE_LEGEND_MARK_PX,
                flexShrink: 0,
                mt: '1px',
                backgroundColor: colors[index % colors.length],
              }}
            />
            <Box
              className="pie-chart-legend-label-text"
              sx={{
                flex: 1,
                minWidth: 0,
                whiteSpace: 'pre-line',
                wordBreak: 'break-word',
                hyphens: 'auto',
              }}
            >
              {item.label}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
